class ProductSectionController {
  constructor(section) {
    this.section = section;
    this.product = this.parseProduct();
    this.form = section.querySelector('[data-product-form]');
    this.gallery = section.querySelector('[data-product-gallery]');
    this.viewport = section.querySelector('[data-gallery-viewport]');
    this.stickyBar = section.querySelector('[data-sticky-bar]');
    this.optionButtons = [...section.querySelectorAll('[data-option-button]')];
    this.optionSelects = [...section.querySelectorAll('[data-option-select]')];
    this.variantIdInputs = [...section.querySelectorAll('[data-variant-id-input]')];
    this.quantityInput = section.querySelector('[data-quantity-input]');
    this.accordionTriggers = [...section.querySelectorAll('[data-accordion-trigger]')];
    this.price = section.querySelector('[data-product-price]');
    this.comparePrice = section.querySelector('[data-product-compare-price]');
    this.savingsBadge = section.querySelector('[data-product-savings-badge]');
    this.priceWrap = section.querySelector('[data-savings-format]');
    this.savingsFormat = this.priceWrap?.dataset.savingsFormat || 'percent';
    this.savingsTemplatePercent = this.priceWrap?.dataset.savingsTemplatePercent || 'Save [[value]] today';
    this.savingsTemplateAmount = this.priceWrap?.dataset.savingsTemplateAmount || 'Save [[value]] today';
    this.inventory = section.querySelector('[data-product-inventory]');
    this.sku = section.querySelector('[data-product-sku]');
    this.stickyPrice = section.querySelector('[data-sticky-price]');
    this.stickyComparePrice = section.querySelector('[data-sticky-compare-price]');
    this.stickyImage = section.querySelector('[data-sticky-image]');
    this.stickyOptionSelects = [...section.querySelectorAll('[data-sticky-option-select]')];
    this.addToCart = section.querySelector('[data-add-to-cart]');
    this.addToCartLabel = section.querySelector('[data-add-to-cart-label]');
    this.stickySubmit = section.querySelector('[data-sticky-submit]');
    this.stickySubmitLabel = section.querySelector('[data-sticky-submit-label]');
    this.addToCartText = section.dataset.addToCartLabel || 'Add to cart';
    this.soldOutText = section.dataset.soldOutLabel || 'Sold out';
    this.unavailableText = section.dataset.unavailableLabel || 'Unavailable';
    this.availableToOrderText = section.dataset.availableToOrderLabel || 'Available to order';
    this.inStockTemplate = section.dataset.inStockTemplate || '[[count]] in stock';
    this.skuTemplate = section.dataset.skuTemplate || 'SKU: [[value]]';
    this.currentVariant = this.findInitialVariant();

    if (!this.product || !this.form) return;

    this.bindOptionInputs();
    this.bindQuantityControls();
    this.bindGallery();
    this.setupPeekLoop();
    this.bindStickyBar();
    this.bindAccordions();
    this.applySelections(this.currentVariant?.options || this.getSelectedOptions());
    this.updateVariantUI(this.currentVariant);
  }

  parseProduct() {
    const node = this.section.querySelector('[data-product-json]');
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('Unable to parse product JSON', error);
      return null;
    }
  }

  findInitialVariant() {
    const currentId = Number(this.form.querySelector('[name="id"]')?.value);
    return (
      this.product.variants.find((variant) => variant.id === currentId) ||
      this.product.selected_or_first_available_variant ||
      this.product.variants[0]
    );
  }

  bindOptionInputs() {
    this.optionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) return;
        const optionName = button.dataset.optionName;
        const optionValue = button.dataset.optionValue;
        this.handleVariantChange(optionName, optionValue);
      });
    });

    this.optionSelects.forEach((select) => {
      select.addEventListener('change', () => {
        this.handleVariantChange(select.dataset.optionName, select.value);
      });
    });

    this.stickyOptionSelects.forEach((select) => {
      select.addEventListener('change', () => {
        this.handleVariantChange(select.dataset.optionName, select.value);
      });
    });
  }

  updateSelectedValue(optionName, optionValue) {
    const option = this.product.options.findIndex((name) => name === optionName);
    const label = this.section.querySelector(`[data-selected-option="${option + 1}"]`);
    if (label) label.textContent = optionValue;
  }

  getSelectedOptions() {
    return this.product.options.map((optionName) => {
      const select = this.optionSelects.find((item) => item.dataset.optionName === optionName);
      if (select) return select.value;

      const button = this.optionButtons.find(
        (item) => item.dataset.optionName === optionName && item.classList.contains('is-active')
      );
      return button?.dataset.optionValue;
    });
  }

  handleVariantChange(optionName, optionValue) {
    const optionIndex = this.product.options.findIndex((name) => name === optionName);
    const selectedOptions = this.getSelectedOptions();

    if (optionIndex >= 0) {
      selectedOptions[optionIndex] = optionValue;
    }

    const nextVariant = this.resolveVariantFromSelection(selectedOptions, optionIndex);
    if (!nextVariant) return;

    const normalizedSelections = [...nextVariant.options];
    this.applySelections(normalizedSelections);
    this.currentVariant = nextVariant;
    this.updateVariantUI(nextVariant);
  }

  applySelections(selectedOptions) {
    this.product.options.forEach((optionName, index) => {
      const selectedValue = selectedOptions[index];
      const relatedButtons = this.optionButtons.filter((button) => button.dataset.optionName === optionName);

      relatedButtons.forEach((button) => {
        const isActive = button.dataset.optionValue === selectedValue;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      const select = this.optionSelects.find((item) => item.dataset.optionName === optionName);
      if (select) select.value = selectedValue;

      const stickySelect = this.stickyOptionSelects.find(
        (item) => item.dataset.optionName === optionName
      );

      if (stickySelect) {
        stickySelect.value = selectedValue;
      }

      this.updateSelectedValue(optionName, selectedValue);
    });

    this.updateOptionAvailability(selectedOptions);
  }

  updateOptionAvailability(selectedOptions) {
    this.product.options.forEach((optionName, index) => {
      this.optionButtons
        .filter((button) => button.dataset.optionName === optionName)
        .forEach((button) => {
          const isReachable = this.isOptionValueReachable(index, button.dataset.optionValue, selectedOptions);
          button.disabled = !isReachable;
          button.classList.toggle('is-unavailable', !isReachable);
          button.setAttribute('aria-disabled', isReachable ? 'false' : 'true');
          if (!isReachable) {
            button.title = button.dataset.unavailableLabel || 'Sold out';
          } else {
            button.removeAttribute('title');
          }
        });

      const select = this.optionSelects.find(
        (item) => item.dataset.optionName === optionName
      );

      if (select) {
        [...select.options].forEach((option) => {
          option.disabled = !this.isOptionValueReachable(
            index,
            option.value,
            selectedOptions
          );
        });
      }

      const stickySelect = this.stickyOptionSelects.find(
        (item) => item.dataset.optionName === optionName
      );

      if (stickySelect) {
        [...stickySelect.options].forEach((option) => {
          option.disabled = !this.isOptionValueReachable(
            index,
            option.value,
            selectedOptions
          );
        });
      }
    });
  }

  isOptionValueReachable(index, optionValue, selectedOptions) {
    return this.product.variants.some((variant) =>
      variant.options.every((value, variantIndex) => {
        if (variantIndex === index) return value === optionValue;

        const selectedValue = selectedOptions[variantIndex];
        return !selectedValue || value === selectedValue;
      })
    );
  }

  resolveVariantFromSelection(selectedOptions, changedIndex = 0) {
    const exactVariant = this.findVariantByOptions(selectedOptions);
    if (exactVariant) return exactVariant;

    const rankedVariants = this.product.variants
      .filter((variant) => {
        if (changedIndex < 0) return true;
        return variant.options[changedIndex] === selectedOptions[changedIndex];
      })
      .map((variant) => ({
        variant,
        score: variant.options.reduce((total, value, index) => total + (value === selectedOptions[index] ? 1 : 0), 0)
      }))
      .sort((left, right) => {
        if (right.score !== left.score) return right.score - left.score;
        if (left.variant.available !== right.variant.available) {
          return Number(right.variant.available) - Number(left.variant.available);
        }

        return 0;
      });

    return rankedVariants[0]?.variant || null;
  }

  findVariantByOptions(selectedOptions) {
    return this.product.variants.find((variant) =>
      variant.options.every((option, index) => option === selectedOptions[index])
    );
  }

  updateVariantUI(variant) {
    this.variantIdInputs.forEach((input) => {
      input.value = variant.id;
    });

    if (this.price) this.price.textContent = this.formatMoney(variant.price);
    if (this.stickyPrice) this.stickyPrice.textContent = this.formatMoney(variant.price);

    if (this.stickyComparePrice) {
      if (variant.compare_at_price > variant.price) {
        this.stickyComparePrice.hidden = false;
        this.stickyComparePrice.textContent = this.formatMoney(variant.compare_at_price);
      } else {
        this.stickyComparePrice.hidden = true;
        this.stickyComparePrice.textContent = '';
      }
    }

    if (this.stickyImage) {
      const stickyImageSrc =
        variant.featured_image?.src ||
        variant.featured_media?.preview_image?.src;

      if (stickyImageSrc) {
        this.stickyImage.src = stickyImageSrc;
      }
    }

    if (this.comparePrice) {
      if (variant.compare_at_price > variant.price) {
        this.comparePrice.hidden = false;
        this.comparePrice.textContent = this.formatMoney(variant.compare_at_price);
      } else {
        this.comparePrice.hidden = true;
        this.comparePrice.textContent = '';
      }
    }

    if (this.savingsBadge) {
      if (variant.compare_at_price > variant.price) {
        const savingsAmount = variant.compare_at_price - variant.price;
        const savingsPercent = Math.round((savingsAmount / variant.compare_at_price) * 100);
        this.savingsBadge.hidden = false;
        this.savingsBadge.textContent =
          this.savingsFormat === 'amount'
            ? this.savingsTemplateAmount.replace('[[value]]', this.formatMoney(savingsAmount))
            : this.savingsTemplatePercent.replace('[[value]]', `${savingsPercent}%`);
      } else {
        this.savingsBadge.hidden = true;
        this.savingsBadge.textContent = '';
      }
    }

    if (this.inventory) {
      this.inventory.textContent = variant.available
        ? variant.inventory_quantity > 0
          ? this.inStockTemplate.replace('[[count]]', variant.inventory_quantity)
          : this.availableToOrderText
        : this.unavailableText;
    }

    if (this.sku) {
      this.sku.textContent = variant.sku ? this.skuTemplate.replace('[[value]]', variant.sku) : '';
    }

    if (this.addToCart && this.addToCartLabel) {
      this.addToCart.disabled = !variant.available;
      this.addToCartLabel.textContent = variant.available ? this.addToCartText : this.soldOutText;
    }

    if (this.stickySubmit) {
      this.stickySubmit.disabled = !variant.available;
    }

    if (this.stickySubmitLabel) {
      this.stickySubmitLabel.textContent =
        variant.available ? this.addToCartText : this.soldOutText;
    }

    if (variant.featured_media?.id) {
      this.showMedia(variant.featured_media.id);
    }

    const nextUrl = `${this.section.dataset.productUrl}?variant=${variant.id}`;
    window.history.replaceState({ variantId: variant.id }, '', nextUrl);
  }

 bindGallery() {
    if (!this.gallery || !this.viewport) return;

    const thumbButtons = [...this.section.querySelectorAll('[data-gallery-thumb]')];
    const items = [...this.section.querySelectorAll('[data-media-id]')];

    const modal = this.section.querySelector('[data-gallery-modal]');
    const modalStage = this.section.querySelector('[data-gallery-modal-body]');
    const modalTemplates = [...this.section.querySelectorAll('[data-gallery-modal-template]')];

    const closeModal = this.section.querySelector('[data-gallery-close]');
    const modalPrev = this.section.querySelector('[data-gallery-modal-prev]');
    const modalNext = this.section.querySelector('[data-gallery-modal-next]');

    const mediaIds = items.map((item) => Number(item.dataset.mediaId));

    const prefersReducedMotion =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let modalTrack = null;
    let activeIndex = 0;

    let pointerStartX = null;
    let pointerStartY = null;
    let activePointerId = null;

    let didDrag = false;
    let isSliding = false;
    let isTransitioningModal = false;
    let isImageZoomed = false;


    /*
    * Lightbox zoom / pan
    */
    const ZOOM_SCALE = 2;

    const mobileGalleryMq =
      window.matchMedia('(max-width: 749px)');

    const MOBILE_BASE_SCALE = 1.04;
    const MOBILE_ZOOM_SCALE = 2.3;

    const isMobileGallery = () =>
      mobileGalleryMq.matches;




    let panX = 0;
    let panY = 0;

    let panStartX = 0;
    let panStartY = 0;

    let panOriginX = 0;
    let panOriginY = 0;

    let panPointerId = null;

    let isPanning = false;
    let didPan = false;

    const getSlideWidth = () => {
      const width = modalStage?.getBoundingClientRect().width;

      return width && Number.isFinite(width)
        ? width
        : window.innerWidth;
    };

    /*
    * Normal product thumbnails
    */
    thumbButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.showMedia(Number(button.dataset.targetMediaId));
      });
    });

    const getTemplate = (mediaId) => {
      return modalTemplates.find(
        (template) =>
          Number(template.dataset.galleryModalTemplate) === Number(mediaId)
      );
    };

    const createSlide = (mediaId) => {
      const slide = document.createElement('div');

      slide.className = 'product-gallery__modal-slide';
      slide.dataset.modalSlideId = mediaId;

      const template = getTemplate(mediaId);

      if (template) {
        slide.appendChild(template.content.cloneNode(true));
      }

      /*
      * MOBILE ONLY:
      * Give previous/current/next the exact same base scale
      * BEFORE the user swipes to them.
      */
      if (isMobileGallery()) {
        const image =
          slide.querySelector('.product-gallery__modal-image');

        if (image) {
          image.style.transformOrigin = '50% 50%';
          image.style.transform =
            `translate3d(0, 0, 0) scale(${MOBILE_BASE_SCALE})`;
        }
      }

      return slide;
    };

    const updateSlideClasses = () => {
      if (!modalTrack) return;

      const slides = [...modalTrack.children];

      slides.forEach((slide, index) => {
        slide.classList.remove(
          'product-gallery__modal-slide--previous',
          'product-gallery__modal-slide--current',
          'product-gallery__modal-slide--next'
        );

        if (index === 0) {
          slide.classList.add('product-gallery__modal-slide--previous');
        }

        if (index === 1) {
          slide.classList.add('product-gallery__modal-slide--current');
        }

        if (index === 2) {
          slide.classList.add('product-gallery__modal-slide--next');
        }
      });
    };

    /*
    * Only keep previous, current and next image in the DOM.
    * The existing slides are rotated after navigation instead
    * of rebuilding the whole carousel.
    */
    const buildTrack = (index) => {
      if (!modalStage || !mediaIds.length) return;

      activeIndex = index;

      const previousIndex =
        (activeIndex - 1 + mediaIds.length) % mediaIds.length;

      const nextIndex =
        (activeIndex + 1) % mediaIds.length;

      const track = document.createElement('div');
      track.className = 'product-gallery__modal-track';

      track.appendChild(
        createSlide(mediaIds[previousIndex])
      );

      track.appendChild(
        createSlide(mediaIds[activeIndex])
      );

      track.appendChild(
        createSlide(mediaIds[nextIndex])
      );

      modalStage.replaceChildren(track);

      modalTrack = track;

      updateSlideClasses();

      /*
      * Always centre the CURRENT slide.
      * Track = previous | current | next.
      */
      modalTrack.style.transition = 'none';
      modalTrack.style.transform =
        'translate3d(-33.333333%, 0, 0)';

      /*
      * MOBILE ONLY:
      * fullscreen starts slightly zoomed instead of appearing
      * smaller than the product-page image.
      */
      applyMobileBaseScale();

      /*
      * Force browser to apply the position immediately.
      */
      modalTrack.getBoundingClientRect();
    };

    const currentModalImage = () => {
      return modalTrack?.querySelector(
        '.product-gallery__modal-slide--current .product-gallery__modal-image'
      );
    };

    const applyMobileBaseScale = () => {
      if (!isMobileGallery()) return;

      const image = currentModalImage();

      if (!image) return;

      image.style.transformOrigin = '50% 50%';

      image.style.transform =
        `translate3d(0, 0, 0) scale(${MOBILE_BASE_SCALE})`;
    };

    const pointIsInsideCurrentImage = (clientX, clientY) => {
      const image = currentModalImage();

      if (!image) return false;

      const rect = getRenderedImageRect(image);

      return (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      );
    };


    const clamp = (value, min, max) => {
      return Math.min(max, Math.max(min, value));
    };

    const clampModalPan = () => {
      const image = currentModalImage();

      if (!image) return;

      /*
      * =====================================================
      * MOBILE ONLY
      * =====================================================
      */
      if (isMobileGallery()) {
        const stageRect =
          modalStage.getBoundingClientRect();

        const boxWidth =
          image.clientWidth;

        const boxHeight =
          image.clientHeight;

        const naturalWidth =
          image.naturalWidth || boxWidth;

        const naturalHeight =
          image.naturalHeight || boxHeight;

        if (
          !boxWidth ||
          !boxHeight ||
          !naturalWidth ||
          !naturalHeight
        ) {
          return;
        }

        /*
        * Calculate actual visible image dimensions when using
        * object-fit: contain.
        */
        const containScale = Math.min(
          boxWidth / naturalWidth,
          boxHeight / naturalHeight
        );

        const renderedWidth =
          naturalWidth * containScale;

        const renderedHeight =
          naturalHeight * containScale;

        const zoomedWidth =
          renderedWidth * MOBILE_ZOOM_SCALE;

        const zoomedHeight =
          renderedHeight * MOBILE_ZOOM_SCALE;

        const maxPanX = Math.max(
          0,
          (zoomedWidth - stageRect.width) / 2
        );

        const maxPanY = Math.max(
          0,
          (zoomedHeight - stageRect.height) / 2
        );

        panX = clamp(
          panX,
          -maxPanX,
          maxPanX
        );

        panY = clamp(
          panY,
          -maxPanY,
          maxPanY
        );

        return;
      }

      /*
      * =====================================================
      * DESKTOP — EXACT EXISTING BEHAVIOUR
      * =====================================================
      */

      const baseWidth = image.offsetWidth;
      const baseHeight = image.offsetHeight;

      const maxPanX =
        (baseWidth * (ZOOM_SCALE - 1)) / 2;

      const maxPanY =
        (baseHeight * (ZOOM_SCALE - 1)) / 2;

      panX = clamp(
        panX,
        -maxPanX,
        maxPanX
      );

      panY = clamp(
        panY,
        -maxPanY,
        maxPanY
      );
    };
    

    const applyModalZoomTransform = (animate = true) => {
      const image = currentModalImage();

      if (!image) return;

      image.style.transition =
        animate && !prefersReducedMotion
          ? 'transform 300ms cubic-bezier(0.22, 1, 0.36, 1)'
          : 'none';

      image.style.transformOrigin = '50% 50%';

      /*
      * MOBILE ONLY
      */
      if (isMobileGallery()) {
        const scale =
          isImageZoomed
            ? MOBILE_ZOOM_SCALE
            : MOBILE_BASE_SCALE;

        image.style.transform =
          `translate3d(${panX}px, ${panY}px, 0) ` +
          `scale(${scale})`;

        return;
      }

      /*
      * DESKTOP — unchanged.
      */
      if (isImageZoomed) {
        image.style.transform =
          `translate3d(${panX}px, ${panY}px, 0) ` +
          `scale(${ZOOM_SCALE})`;
      } else {
        image.style.transform =
          'translate3d(0, 0, 0) scale(1)';
      }
    };

    const resetModalZoom = (instant = false) => {
      const image = currentModalImage();

      isImageZoomed = false;

      panX = 0;
      panY = 0;

      isPanning = false;

      modalStage?.classList.remove(
        'is-zoomed',
        'is-panning'
      );

      if (!image) return;

      /*
      * Crucially: transform-origin remains centred.
      * We no longer change it during the animation.
      */
      applyModalZoomTransform(!instant);
    };

    const toggleModalZoom = (clientX, clientY) => {
      const image = currentModalImage();

      if (
        !image ||
        isSliding ||
        isTransitioningModal
      ) {
        return;
      }

      /*
      * Click while already zoomed = smooth zoom out.
      */
      if (isImageZoomed) {
        resetModalZoom(false);
        return;
      }

      const rect =
        getRenderedImageRect(image);

      if (
        clientX < rect.left ||
        clientX > rect.right ||
        clientY < rect.top ||
        clientY > rect.bottom
      ) {
        return;
      }

      /*
      * Keep transform-origin permanently centred.
      * Instead, move the image towards the area clicked.
      *
      * This removes the transform-origin jump/glitch.
      */
      const centerX =
        rect.left + rect.width / 2;

      const centerY =
        rect.top + rect.height / 2;

      const zoomRatio =
        isMobileGallery()
          ? MOBILE_ZOOM_SCALE / MOBILE_BASE_SCALE
          : ZOOM_SCALE;

      panX =
        -(clientX - centerX) *
        (zoomRatio - 1);

      panY =
        -(clientY - centerY) *
        (zoomRatio - 1);

      isImageZoomed = true;

      clampModalPan();

      modalStage?.classList.add(
        'is-zoomed'
      );

      applyModalZoomTransform(true);
    };



    

    const waitForImage = async (image) => {
      if (!image) return;

      if (!image.complete) {
        await new Promise((resolve) => {
          image.addEventListener('load', resolve, { once: true });

          window.setTimeout(resolve, 350);
        });
      }

      if (typeof image.decode === 'function') {
        try {
          await image.decode();
        } catch (error) {
          // Browser already has enough image data to continue.
        }
      }
    };

    const getRenderedImageRect = (image) => {
      const box = image.getBoundingClientRect();

      const naturalWidth =
        image.naturalWidth ||
        Number(image.getAttribute('width')) ||
        box.width;

      const naturalHeight =
        image.naturalHeight ||
        Number(image.getAttribute('height')) ||
        box.height;

      if (
        !naturalWidth ||
        !naturalHeight ||
        !box.width ||
        !box.height
      ) {
        return box;
      }

      const objectFit =
        window.getComputedStyle(image).objectFit;

      if (
        objectFit !== 'contain' &&
        objectFit !== 'scale-down'
      ) {
        return box;
      }

      const scale = Math.min(
        box.width / naturalWidth,
        box.height / naturalHeight
      );

      const width = naturalWidth * scale;
      const height = naturalHeight * scale;

      const left =
        box.left + (box.width - width) / 2;

      const top =
        box.top + (box.height - height) / 2;

      return {
        left,
        top,
        width,
        height,
        right: left + width,
        bottom: top + height
      };
    };

    /*
    * Shared image transition clone.
    */
    const createTransitionClone = (image, rect) => {
      const clone = document.createElement('img');

      clone.src = image.currentSrc || image.src;
      clone.alt = '';

      clone.className =
        'product-gallery__zoom-transition-image';

      Object.assign(clone.style, {
        position: 'fixed',

        left: `${rect.left}px`,
        top: `${rect.top}px`,

        width: `${rect.width}px`,
        height: `${rect.height}px`,

        margin: '0',

        zIndex: '9999',

        objectFit:
          window.getComputedStyle(image).objectFit || 'contain',

        pointerEvents: 'none',

        transform: 'translate3d(0, 0, 0) scale(1, 1)',
        transformOrigin: 'top left',

        willChange: 'transform, opacity'
      });

      /*
      * IMPORTANT:
      * The modal lives in the browser top layer.
      * The transition image must therefore live inside the modal.
      */
      modal.appendChild(clone);

      return clone;
    };

    /*
    * OPEN
    * Animate from the exact product image position
    * into the fullscreen image.
    */
    const openModal = async (item) => {
      if (
        !modal ||
        !modalStage ||
        !item ||
        isTransitioningModal
      ) {
        return;
      }

      const mediaId = Number(item.dataset.mediaId);
      const index = mediaIds.indexOf(mediaId);

      if (index < 0) return;

      const sourceImage =
       item.querySelector('.product-gallery__image');

     if (!sourceImage) return;

     isTransitioningModal = true;

      /*
      * Exact visible position on the product page.
      */
      const sourceRect =
        getRenderedImageRect(sourceImage);

      /*
      * Build previous | current | next before opening.
      */
      buildTrack(index);

      const targetImage = currentModalImage();

      if (!targetImage) {
        isTransitioningModal = false;
        return;
     }

      /*
      * Hide the real fullscreen image.
      * The clone will visually replace it during the zoom.
      */
      targetImage.style.opacity = '0';

      /*
      * Create the clone BEFORE opening the dialog.
      * This prevents the white pause.
      */
      const clone =
        createTransitionClone(
          sourceImage,
          sourceRect
        );

      modal.classList.add(
        'is-opening',
        'is-transitioning'
      );

      document.documentElement.classList.add(
        'product-gallery-modal-open'
      );

      document.body.classList.add(
        'product-gallery-modal-open'
      );

      modal.showModal();

      /*
      * Give the browser one rendered frame to calculate
      * the fullscreen geometry.
      */
      await new Promise((resolve) =>
        requestAnimationFrame(() =>
          requestAnimationFrame(resolve)
        )
      );

      let targetRect =
        getRenderedImageRect(targetImage);

      /*
      * Normally Shopify's width/height attributes mean the
      * geometry is already available before the 2400px image loads.
      * Only wait first if its geometry is genuinely unavailable.
      */
      if (!targetRect.width || !targetRect.height) {
        await waitForImage(targetImage);
        targetRect =
         getRenderedImageRect(targetImage);
      }

      if (
        prefersReducedMotion ||
        !targetRect.width ||
        !targetRect.height
     ) {
        targetImage.style.opacity = '';

        clone.remove();

        modal.classList.remove(
          'is-opening',
          'is-transitioning'
        );

        isTransitioningModal = false;

        closeModal?.focus();

        return;
      }

      /*
      * Fade in the white fullscreen environment while
      * the image itself grows.
      */
      requestAnimationFrame(() => {
        modal.classList.remove('is-opening');
      });

     /*
      * FLIP transform.
      * Only transform is animated -> compositor/GPU animation.
      */
      
      const scale = Math.min(
        targetRect.width / sourceRect.width,
        targetRect.height / sourceRect.height
      );

      const finalWidth =
        sourceRect.width * scale;

      const finalHeight =
        sourceRect.height * scale;

      const finalLeft =
       targetRect.left +
        (targetRect.width - finalWidth) / 2;

      const finalTop =
        targetRect.top +
        (targetRect.height - finalHeight) / 2;

      const deltaX =
        finalLeft - sourceRect.left;

      const deltaY =
        finalTop - sourceRect.top;

      /*
      * Start loading/decoding the final large image in parallel.
      * Do NOT pause the opening animation for it.
      */
      const targetReady =
        waitForImage(targetImage);

      const animation = clone.animate(
        [
          {
           transform:
              'translate3d(0, 0, 0) scale(1, 1)',
            opacity: 1
          },
          {
           transform:
              `translate3d(${deltaX}px, ${deltaY}px, 0) ` +
              `scale(${scale})`,
            opacity: 1
         }
        ],
        {
          duration: 420,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          fill: 'forwards'
        }
      );

      try {
        await animation.finished;
      } catch (error) {
        // Animation can be cancelled on section unload.
      }

      /*
      * If the high-resolution image still needs a moment,
      * leave the clone sitting exactly on top of it.
      */
      await targetReady;

      targetImage.style.opacity = '1';

      /*
      * Let the real image paint underneath the clone first.
      */
      await new Promise((resolve) =>
        requestAnimationFrame(resolve)
      );

      clone.remove();

      targetImage.style.opacity = '';

      modal.classList.remove('is-transitioning');

      isTransitioningModal = false;

      closeModal?.focus();
    };




    /*
    * Click the actual product image.
    */
    items.forEach((item) => {
      if (item.dataset.mediaType !== 'image') return;

      item.addEventListener('click', (event) => {
        if (
          event.target.closest(
            'button, a, video, iframe, model-viewer'
          )
        ) {
          return;
        }

        openModal(item);
      });
    });

    /*
    * Rotate existing slides after a completed transition.
    * The image that just moved into the centre is NOT rebuilt.
    */
    const finishNavigation = (direction) => {
      if (!modalTrack || !modalStage) return;

      activeIndex =
        (activeIndex + direction + mediaIds.length) %
        mediaIds.length;

      if (direction > 0) {
        /*
        * Old current becomes previous.
        * Old next remains in the DOM and becomes current.
        */
        modalTrack.firstElementChild?.remove();

        const newNextIndex =
          (activeIndex + 1) % mediaIds.length;

        modalTrack.appendChild(
          createSlide(mediaIds[newNextIndex])
        );
      } else {
        /*
        * Old previous remains in the DOM and becomes current.
        */
        modalTrack.lastElementChild?.remove();

        const newPreviousIndex =
          (activeIndex - 1 + mediaIds.length) %
          mediaIds.length;

        modalTrack.insertBefore(
          createSlide(mediaIds[newPreviousIndex]),
          modalTrack.firstElementChild
        );
      }

      updateSlideClasses();

      applyMobileBaseScale();

      const width = getSlideWidth();

      modalTrack.style.transition = 'none';
      modalTrack.style.transform =
        `translate3d(${-width}px, 0, 0)`;

      /*
      * Force the reset before allowing another swipe.
      */
      modalTrack.getBoundingClientRect();

      isSliding = false;
    };

    /*
    * Snap to next / previous image.
    */
    const navigateModal = (direction, fromDrag = false) => {
      if (
        !modalTrack ||
        !modalStage ||
        isSliding ||
        mediaIds.length < 2
      ) {
        return;
      }

      resetModalZoom(true);

      isSliding = true;

      const width = getSlideWidth();

      let finished = false;
      let fallbackTimer = null;

      const completeNavigation = () => {
        if (finished) return;

        finished = true;

        if (fallbackTimer) {
          window.clearTimeout(fallbackTimer);
        }

        finishNavigation(direction);
      };

      const runTransition = () => {
       if (!modalTrack) {
          isSliding = false;
          return;
        }

        modalTrack.style.transition =
          prefersReducedMotion
            ? 'none'
            : 'transform 340ms cubic-bezier(0.22, 1, 0.36, 1)';

        modalTrack.style.transform =
          direction > 0
            ? `translate3d(${-2 * width}px, 0, 0)`
            : 'translate3d(0px, 0, 0)';

        if (prefersReducedMotion) {
          completeNavigation();
          return;
        }

        modalTrack.addEventListener(
          'transitionend',
          completeNavigation,
          { once: true }
        );

        /*
        * Safety net:
        * if transitionend ever gets missed,
        * the gallery can never remain locked.
        */
        fallbackTimer = window.setTimeout(
          completeNavigation,
          450
        );
      };

      if (fromDrag) {
        modalTrack.style.transition = 'none';

        /*
         * Commit exact drag position first.
        */
        modalTrack.getBoundingClientRect();

        requestAnimationFrame(runTransition);
      } else {
        runTransition();
      }
    };

    modalPrev?.addEventListener('click', () => {
      navigateModal(-1);
    });

    modalNext?.addEventListener('click', () => {
      navigateModal(1);
    });

    /*
    * DRAG
    * The complete track follows the mouse/touch exactly.
    */
    modalStage?.addEventListener(
      'pointerdown',
      (event) => {
        if (
          !modalTrack ||
          isSliding ||
          isTransitioningModal ||
          isImageZoomed
        ) {
          return;
        }

        if (
          event.pointerType === 'mouse' &&
          event.button !== 0
        ) {
          return;
        }

        if (
          event.target.closest(
            'button, video, iframe, model-viewer'
          )
        ) {
          return;
        }

        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
        activePointerId = event.pointerId;

        didDrag = false;

        modalTrack.style.transition = 'none';

        modalStage.setPointerCapture?.(
          event.pointerId
        );
      }
    );

    modalStage?.addEventListener(
      'pointermove',
      (event) => {
        if (
          pointerStartX === null ||
          pointerStartY === null ||
          activePointerId !== event.pointerId ||
          !modalTrack
        ) {
          return;
        }

        const deltaX =
          event.clientX - pointerStartX;

        const deltaY =
          event.clientY - pointerStartY;

        if (
         Math.abs(deltaX) > 4 &&
          Math.abs(deltaX) > Math.abs(deltaY)
        ) {
          didDrag = true;

          modalStage.classList.add('is-dragging');

         const width = getSlideWidth();

          modalTrack.style.transform =
            `translate3d(${-width + deltaX}px, 0, 0)`;

          event.preventDefault();
        }
      }
    );

    const finishDrag = (event) => {
      if (
        pointerStartX === null ||
        pointerStartY === null ||
        activePointerId !== event.pointerId ||
        !modalTrack ||
        !modalStage
      ) {
        return;
      }

      const deltaX =
        event.clientX - pointerStartX;

      const deltaY =
       event.clientY - pointerStartY;

      const width = getSlideWidth();

      const threshold =
        Math.max(70, width * 0.11);

      const validSwipe =
        Math.abs(deltaX) >= threshold &&
        Math.abs(deltaX) > Math.abs(deltaY);

      pointerStartX = null;
      pointerStartY = null;
      activePointerId = null;

      modalStage.classList.remove('is-dragging');

      if (validSwipe) {
        /*
        * Explicitly preserve the EXACT release position.
        */
        modalTrack.style.transition = 'none';
        modalTrack.style.transform =
          `translate3d(${-width + deltaX}px, 0, 0)`;

        /*
        * Force that position to be committed before snapping onward.
        */
        modalTrack.getBoundingClientRect();

        navigateModal(
          deltaX < 0 ? 1 : -1,
          true
        );
      } else {

        /*
        * Not far enough: smoothly return to centre.
        */
        modalTrack.style.transition =
          prefersReducedMotion
            ? 'none'
            : 'transform 300ms cubic-bezier(0.22, 1, 0.36, 1)';

        modalTrack.style.transform =
          `translate3d(${-width}px, 0, 0)`;
      }

      window.setTimeout(() => {
        didDrag = false;
      }, 0);
    };

    modalStage?.addEventListener(
      'pointerup',
      finishDrag
    );

    modalStage?.addEventListener(
      'pointercancel',
      () => {
        pointerStartX = null;
        pointerStartY = null;
        activePointerId = null;
        didDrag = false;

        modalStage.classList.remove('is-dragging');

        if (modalTrack && modalStage) {
           modalTrack.style.transition =
            'transform 250ms ease';

          modalTrack.style.transform =
            'translate3d(-33.333333%, 0, 0)';
        }
      }
    );

    /*
    * =========================================================
    * MOBILE PAN WHILE ZOOMED
    * =========================================================
    */

    modalStage?.addEventListener(
      'pointerdown',
      (event) => {
        if (
          !isMobileGallery() ||
          !isImageZoomed ||
          event.pointerType !== 'touch' ||
          isTransitioningModal
        ) {
          return;
        }

        isPanning = true;
        didPan = false;

        panPointerId = event.pointerId;

        panStartX = event.clientX;
        panStartY = event.clientY;

        panOriginX = panX;
        panOriginY = panY;

        modalStage.classList.add(
          'is-panning'
        );

        try {
          modalStage.setPointerCapture(
            event.pointerId
          );
        } catch (error) {
          // Pointer capture is optional.
        }

        event.preventDefault();
      }
    );

    /*
    * =========================================================
    * DESKTOP PAN WHILE ZOOMED
    * =========================================================
    */

    modalStage?.addEventListener(
      'pointerdown',
      (event) => {
        /*
        * Mobile touch behaviour behandelen we later apart.
        */
        if (
          !isImageZoomed ||
          event.pointerType !== 'mouse' ||
          event.button !== 0 ||
          isTransitioningModal
        ) {
          return;
        }

        isPanning = true;
        didPan = false;

        panPointerId = event.pointerId;

        panStartX = event.clientX;
        panStartY = event.clientY;

        panOriginX = panX;
        panOriginY = panY;

        modalStage.classList.add(
          'is-panning'
        );

        try {
          modalStage.setPointerCapture(
            event.pointerId
          );
        } catch (error) {
          // Pointer capture is optional.
        }

        event.preventDefault();
      }
    );

    modalStage?.addEventListener(
      'pointermove',
      (event) => {
        if (
          !isPanning ||
          event.pointerId !== panPointerId
        ) {
          return;
        }

        const deltaX =
          event.clientX - panStartX;

        const deltaY =
          event.clientY - panStartY;

        if (
          Math.abs(deltaX) > 3 ||
          Math.abs(deltaY) > 3
        ) {
          didPan = true;
        }

        panX =
          panOriginX + deltaX;

        panY =
          panOriginY + deltaY;

        clampModalPan();

        applyModalZoomTransform(false);

        event.preventDefault();
      }
    );

    const finishModalPan = (event) => {
      if (
        !isPanning ||
        event.pointerId !== panPointerId
      ) {
        return;
      }

      isPanning = false;

      modalStage?.classList.remove(
        'is-panning'
      );

      try {
        modalStage.releasePointerCapture(
          event.pointerId
        );
      } catch (error) {
        // Pointer capture may already be released.
      }

      panPointerId = null;

      /*
      * Leave didPan true until after the click event.
      * Otherwise releasing the mouse would immediately
      * trigger "click = zoom out".
      */
      window.setTimeout(() => {
        didPan = false;
      }, 0);
    };

    modalStage?.addEventListener(
      'pointerup',
      finishModalPan
    );

    modalStage?.addEventListener(
      'pointercancel',
      finishModalPan
    );




    /*
    * Make the normal product gallery show the image that is
    * currently open before shrinking the lightbox back to it.
    */
    const syncNormalGalleryInstant = (mediaId) => {
      const target = this.section.querySelector(
        `[data-media-id="${mediaId}"]`
      );

      if (!target || !this.viewport) return null;

      const viewportRect =
        this.viewport.getBoundingClientRect();

      const targetRect =
        target.getBoundingClientRect();

      const targetLeft =
        targetRect.left -
        viewportRect.left +
        this.viewport.scrollLeft;

      this.viewport.scrollTo({
        left: targetLeft,
       behavior: 'auto'
      });

      items.forEach((item) => {
        item.classList.toggle(
          'is-active',
         Number(item.dataset.mediaId) === mediaId
       );
      });

      thumbButtons.forEach((button) => {
       button.classList.toggle(
          'is-active',
          Number(button.dataset.targetMediaId) === mediaId
        );
      });

     return target;
    };

    /*
    * CLOSE
    * Reverse the same zoom animation back into the product image.
    */
    const closeModalAnimated = async () => {
      if (
        !modal?.open ||
        isTransitioningModal
      ) {
       return;
     }

      resetModalZoom(true);

      const currentMediaId =
        mediaIds[activeIndex];

      const modalImage =
        currentModalImage();

      if (
        !modalImage ||
        prefersReducedMotion
      ) {
        modal.close();
        return;
      }

      isTransitioningModal = true;

      /*
      * Make sure the normal product gallery underneath
      * is already showing the same image.
      */
      const targetItem =
        syncNormalGalleryInstant(currentMediaId);

      await new Promise((resolve) =>
       requestAnimationFrame(resolve)
      );

      const targetImage =
        targetItem?.querySelector(
          '.product-gallery__image'
       );

      if (!targetImage) {
        modal.close();

        isTransitioningModal = false;

        return;
      }

      const startRect =
         getRenderedImageRect(modalImage);

      const targetRect =
         getRenderedImageRect(targetImage);

      /*
      * Clone stays INSIDE the top-layer dialog.
      */
      const clone =
        createTransitionClone(
          modalImage,
          startRect
        );

      modalImage.style.opacity = '0';

      modal.classList.add(
        'is-closing',
        'is-transitioning'
      );

     const scale = Math.min(
        targetRect.width / startRect.width,
        targetRect.height / startRect.height
      );

      const finalWidth =
        startRect.width * scale;

      const finalHeight =
        startRect.height * scale;

      const finalLeft =
        targetRect.left +
        (targetRect.width - finalWidth) / 2;

      const finalTop =
        targetRect.top +
        (targetRect.height - finalHeight) / 2;

      const deltaX =
        finalLeft - startRect.left;

      const deltaY =
        finalTop - startRect.top;

      const animation = clone.animate(
        [
          {
            transform:
              'translate3d(0, 0, 0) scale(1, 1)',
            opacity: 1
          },
          {
            transform:
              `translate3d(${deltaX}px, ${deltaY}px, 0) ` +
              `scale(${scale})`,
            opacity: 1
          }
        ],
        {
          duration: 380,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          fill: 'forwards'
        }
      );

      try {
        await animation.finished;
      } catch (error) {
        // Ignore cancelled animations.
      }

      /*
       * Product image is already visible underneath.
       */
      clone.remove();

      modal.close();

      isTransitioningModal = false;
    };

    closeModal?.addEventListener(
      'click',
      closeModalAnimated
    );

    /*
    * Native Escape key uses the same closing animation.
    */
    modal?.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeModalAnimated();
    });

    /*
    * SINGLE CLICK / TAP
    *
    * Image:
    *   normal  -> zoom in
    *   zoomed  -> zoom out
    *
    * Outside actual image:
    *   close lightbox
    */
    modalStage?.addEventListener(
      'click',
      (event) => {
        if (
          didDrag ||
          didPan ||
          isTransitioningModal
        ) {
          return;
        }

        const insideImage =
          pointIsInsideCurrentImage(
            event.clientX,
            event.clientY
          );

        if (insideImage) {
          event.preventDefault();

          toggleModalZoom(
            event.clientX,
            event.clientY
          );

          return;
        }

        /*
        * Click/tap beside the actual image.
        */
        resetModalZoom(true);
        closeModalAnimated();
      }
    );

       /*
    * Keyboard navigation.
    */
    modal?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
       event.preventDefault();
       navigateModal(-1);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        navigateModal(1);
     }
    });

    /*
    * Keep track correctly centred after browser resize.
    */
    window.addEventListener('resize', () => {
      if (
        !modal?.open ||
        !modalTrack
      ) {
        return;
      }

      modalTrack.style.transition = 'none';
      modalTrack.style.transform =
        'translate3d(-33.333333%, 0, 0)';
    });

    modal?.addEventListener('close', () => {
      modalStage?.replaceChildren();

      modalTrack = null;

      pointerStartX = null;
      pointerStartY = null;
      activePointerId = null;

      didDrag = false;
      isSliding = false;
      isImageZoomed = false;

      modalStage?.classList.remove('is-zoomed');

      modal.classList.remove(
        'is-opening',
        'is-closing',
        'is-transitioning'
      );

      document.documentElement.classList.remove(
        'product-gallery-modal-open'
      );

      document.body.classList.remove(
        'product-gallery-modal-open'
      );
    });

    /*
    * Normal gallery active thumbnail tracking.
    */
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find(
          (entry) => entry.isIntersecting
        );

        if (!visible) return;

        const mediaId =
          Number(visible.target.dataset.mediaId);

        items.forEach((item) => {
          item.classList.toggle(
            'is-active',
            item === visible.target
          );
        });

        thumbButtons.forEach((button) => {
          button.classList.toggle(
            'is-active',
            Number(button.dataset.targetMediaId) === mediaId
          );
        });
      },
      {
        root: this.viewport,
        threshold: 0.55
      }
    );

    items.forEach((item) =>
      observer.observe(item)
    );
  }







  setupPeekLoop() {
    const viewport = this.viewport;

    if (!viewport || !this.gallery) return;

    /*
    * Only carousel-based galleries can loop.
    */
    const isCarousel =
      this.gallery.classList.contains(
        'product-gallery--carousel_left_thumbnails'
      ) ||
      this.gallery.classList.contains(
        'product-gallery--carousel_bottom_thumbnails'
      );

    if (!isCarousel) return;

    const reals = [...viewport.children].filter(
      (child) =>
        child.hasAttribute('data-media-id')
    );

    if (reals.length < 2) return;

    /*
    * STRICTLY MOBILE.
    * Tablet / desktop are untouched.
    */
    const mq =
      window.matchMedia('(max-width: 749px)');

    let clones = [];
    let scrollTimer = null;
    let jumping = false;

    const centerOn = (el) => {
      jumping = true;
      const itemLeft = el.getBoundingClientRect().left - viewport.getBoundingClientRect().left + viewport.scrollLeft;
      viewport.scrollTo({ left: itemLeft - (viewport.clientWidth - el.offsetWidth) / 2, behavior: 'auto' });
      window.requestAnimationFrame(() => {
        jumping = false;
      });
    };

    const isCentered = (el) => {
      const vpRect = viewport.getBoundingClientRect();
      const rect = el.getBoundingClientRect();
      return Math.abs(rect.left + rect.width / 2 - (vpRect.left + vpRect.width / 2)) < rect.width / 2;
    };

    const handleSettle = () => {
      if (jumping || clones.length !== 2) return;
      if (isCentered(clones[0])) {
        centerOn(reals[reals.length - 1]);
      } else if (isCentered(clones[1])) {
        centerOn(reals[0]);
      }
    };

    const onScroll = () => {
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(handleSettle, 130);
    };

    const enable = () => {
      if (clones.length) return;
      const leadClone = reals[reals.length - 1].cloneNode(true);
      const tailClone = reals[0].cloneNode(true);
      [leadClone, tailClone].forEach((clone) => {
        clone.removeAttribute('data-media-id');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      });
      viewport.insertBefore(leadClone, reals[0]);
      viewport.appendChild(tailClone);
      clones = [leadClone, tailClone];
      viewport.addEventListener('scroll', onScroll, { passive: true });
      window.requestAnimationFrame(() => centerOn(reals[0]));
    };

    const disable = () => {
      if (!clones.length) return;
      viewport.removeEventListener('scroll', onScroll);
      window.clearTimeout(scrollTimer);
      clones.forEach((clone) => clone.remove());
      clones = [];
    };

    const apply = () => {
      if (mq.matches) {
        enable();
      } else {
        disable();
      }
    };

    apply();
    mq.addEventListener('change', apply);
  }

  bindQuantityControls() {
    if (!this.quantityInput) return;

    const decrease = this.section.querySelector('[data-quantity-decrease]');
    const increase = this.section.querySelector('[data-quantity-increase]');

    decrease?.addEventListener('click', () => {
      const nextValue = Math.max(1, Number(this.quantityInput.value || 1) - 1);
      this.quantityInput.value = nextValue;
    });

    increase?.addEventListener('click', () => {
      const nextValue = Math.max(1, Number(this.quantityInput.value || 1) + 1);
      this.quantityInput.value = nextValue;
    });

    this.quantityInput.addEventListener('change', () => {
      const normalized = Math.max(1, Number(this.quantityInput.value || 1));
      this.quantityInput.value = normalized;
    });
  }

  showMedia(mediaId) {
    const target = this.section.querySelector(
      `[data-media-id="${mediaId}"]`
    );

    if (!target || !this.viewport) return;

    const viewportRect = this.viewport.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();

    const targetLeft =
      targetRect.left -
      viewportRect.left +
      this.viewport.scrollLeft;

    this.viewport.scrollTo({
      left: targetLeft,
      behavior: 'smooth'
    });

    this.section.querySelectorAll('[data-media-id]').forEach((item) => {
      item.classList.toggle(
        'is-active',
        Number(item.dataset.mediaId) === Number(mediaId)
      );
    });

    this.section.querySelectorAll('[data-gallery-thumb]').forEach((button) => {
      button.classList.toggle(
        'is-active',
        Number(button.dataset.targetMediaId) === Number(mediaId)
      );
    });
  }

  bindStickyBar() {
    if (!this.stickyBar || !this.addToCart || !this.stickySubmit) return;

    this.stickySubmit.addEventListener('click', () => {
      this.addToCart.click();
    });

    let hasSeenAddToCart = false;
    let rafId = null;

    const updateStickyBarVisibility = () => {
      const buttonBounds = this.addToCart.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const isVisible =
        buttonBounds.bottom > 0 &&
        buttonBounds.top < viewportHeight;

      if (isVisible) hasSeenAddToCart = true;

      const hasScrolledPastButton = buttonBounds.bottom <= 0;
      this.stickyBar.hidden = !(hasSeenAddToCart && hasScrolledPastButton);
      rafId = null;
    };

    const requestStickyBarUpdate = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(updateStickyBarVisibility);
    };

    updateStickyBarVisibility();

    window.addEventListener('scroll', requestStickyBarUpdate, { passive: true });
    window.addEventListener('resize', requestStickyBarUpdate);
  }

  bindAccordions() {
    if (!this.accordionTriggers.length) return;

    this.accordionTriggers.forEach((trigger) => {
      const panelId = trigger.getAttribute('aria-controls');
      const panel = panelId ? this.section.querySelector(`#${CSS.escape(panelId)}`) : null;
      if (!panel) return;

      if (panel.dataset.accordionOpenDefault === 'true') {
        panel.style.height = `${panel.scrollHeight}px`;
      }

      trigger.addEventListener('click', () => {
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        this.setAccordionState(trigger, panel, !isOpen);
      });
    });
  }

  setAccordionState(trigger, panel, shouldOpen) {
    trigger.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
    trigger.classList.toggle('is-open', shouldOpen);
    panel.classList.toggle('is-open', shouldOpen);

    if (shouldOpen) {
      panel.style.height = `${panel.scrollHeight}px`;
    } else {
      panel.style.height = `${panel.scrollHeight}px`;
      requestAnimationFrame(() => {
        panel.style.height = '0px';
      });
    }
  }

  formatMoney(amount) {
    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency
    }).format(amount / 100);
  }

}

document.querySelectorAll('[data-product-section]').forEach((section) => {
  new ProductSectionController(section);
});
